import React, { useContext } from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Typography,
  Chip,
  makeStyles,
} from "@material-ui/core";
import { StyledTableCell, StyledTableRow } from "components/table";
import { ViewWinnerContext } from "../../../contexts/ViewWinnerContext";
import { getLang } from "app/feature/constants";
import moment from "moment-timezone";
import { Skeleton } from "@material-ui/lab";
import { ActionButton } from "components/button";

const useStyles = makeStyles((theme) => ({
  table: {
    tableLayout: "auto",
    whiteSpace: "nowrap",
    borderCollapse: "separate",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
    "& th,td": {
      fontSize: ".75rem",
      padding: theme.spacing(1, 2, 1, 2),
    },
    "& th": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
    },
    "& th:last-child, td:last-child": {
      width: 55,
    },
  },
  tableContainer: {
    borderRadius: "10px",
    marginTop: theme.spacing(2),
  },
  emptyContainer: {
    backgroundColor: theme.palette.background.paper,
    textAlign: "center",
    padding: theme.spacing(9, 0),
  },
  statusUnprocessed: {
    color: "#E21B1B",
    fontWeight: 600,
  },
  statusProcessed: {
    color: "#6AAF68",
    fontWeight: 600,
  },
  statusClaimDone: {
    color: "#A0A0A0",
    fontWeight: 600,
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  statusChipProcessed: {
    backgroundColor: "#E8F5E9",
    color: "#6AAF68",
    border: "1px solid #6AAF68",
  },
  statusChipUnprocessed: {
    backgroundColor: "#FF9800",
    color: "#FFFFFF",
    border: "1px solid #FF9800",
  },
  statusChipClaimDone: {
    backgroundColor: "#F5F5F5",
    color: "#A0A0A0",
    border: "1px solid #A0A0A0",
  },
  statusChipRejected: {
    backgroundColor: "#FFEBEE",
    color: "#D32F2F",
    border: "1px solid #D32F2F",
  },
}));

export default function ViewWinnerDetailsTableComponent() {
  const classes = useStyles();
  const {
    lang,
    companyId,
    filteredWinners,
    selectedRows,
    page,
    rowsPerPage,
    totalCount,
    isFetching,
    activeTab,
    timezone,
    handleSelectRow,
    handleSelectAllRows,
    handleOpenAdminDialog,
    handleChangePage,
    handleChangeRowsPerPage,
    getStatusLabel,
  } = useContext(ViewWinnerContext);

  const getActionItems = (winner) => {
    return [
      {
        label: getLang(lang, "label.VIEW_EDIT_DETAIL"),
        action: () => handleOpenAdminDialog(winner),
      },
    ];
  };

  // Company-specific name postfix logic (from original code)
  const namePostFix = {
    152: [
      "14497e12976a44c8b14fbd45d58c5c5a",
      "1a8d1fb30feb4ca1a45086b6811359bb",
      "30055fba371e4201a541ae074cc1812d",
      "f8e5507ca9374a26bb325f4f8285bd84",
      "3ffbe0eba7c74d7c9eeb4fa55ca4255a",
      "1db635421da244bd8529c3ddaf859308",
      "7738c02825994ac583fdd9ef76f68f9a",
    ],
    146: [
      "6dddb4dc49d04caea9e5b72d1dfdcb5e",
      "c58dac4972e7422bae613922a0df3b6f",
    ],
  };

  const getWinnerName = (winner) => {
    let name = winner.participant?.name || "-";
    if (companyId === 152 || companyId === 146) {
      const postfixValue =
        winner?.form_values?.find(
          (item) =>
            namePostFix[companyId] && namePostFix[companyId].includes(item.uuid)
        )?.value ?? null;
      if (postfixValue) {
        name += postfixValue;
      }
    }
    return name;
  };

  const formatPhoneNumber = (contactNo) => {
    if (!contactNo) return "-";
    // Return full phone number without masking
    return contactNo;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    // Parse the date as UTC first (since API returns UTC times)
    // Then convert to user's timezone (e.g., "Asia/Kuala_Lumpur")
    const userTimezone = timezone || "Asia/Kuala_Lumpur";
    return moment.utc(date).tz(userTimezone).format("D MMM YYYY h:mm A");
  };

  const getPrizeType = (winner) => {
    if (winner.prize_type === 1) {
      return "Physical";
    } else if (winner.prize_type === 2) {
      return "Touch 'n Go";
    } else if (winner.prize_type === 4) {
      return "Loyalty Points";
    }
    return "-";
  };

  const getStatusComponent = (winner) => {
    const label = getStatusLabel(winner);
    let chipClassName = classes.statusChipProcessed;
    let statusText = label;

    if (label === "Unprocessed") {
      chipClassName = classes.statusChipUnprocessed;
      statusText = getLang(lang, "label.UNPROCESSED");
    } else if (label === "Processed") {
      chipClassName = classes.statusChipProcessed;
      statusText = getLang(lang, "label.PROCESSED");
    } else if (label === "Rejected") {
      chipClassName = classes.statusChipRejected;
      statusText = getLang(lang, "label.REJECTED");
    } else {
      chipClassName = classes.statusChipClaimDone;
    }

    return (
      <Chip
        label={statusText}
        size="small"
        className={`${classes.statusChip} ${chipClassName}`}
      />
    );
  };

  // For server-side pagination, display all winners from current page (already filtered by API)
  const displayedWinners = filteredWinners;

  const isAllSelected =
    displayedWinners.length > 0 &&
    displayedWinners.every((w) => selectedRows.includes(w.uuid));

  return (
    <>
      {isFetching ? (
        <Box mt={2}>
          <Skeleton variant="text" animation="wave" height={40} />
          <Skeleton variant="text" animation="wave" height={40} />
          <Skeleton variant="text" animation="wave" height={40} />
        </Box>
      ) : filteredWinners.length > 0 ? (
        <>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {/* Checkbox - Show only in TO PROCESS tab */}
                  {activeTab === 0 && (
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedRows.length > 0 && !isAllSelected
                        }
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAllRows(e.target.checked)}
                      />
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    {getLang(lang, "label.PRIZES")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.PRIZE_TYPE")}
                  </StyledTableCell>
                  {/* Claimed Date - Show only in TO PROCESS tab */}
                  {activeTab === 0 && (
                    <StyledTableCell>
                      {getLang(lang, "label.CLAIMED_DATE")}
                    </StyledTableCell>
                  )}
                  {/* Status - Show only in TO PROCESS tab */}
                  {activeTab === 0 && (
                    <StyledTableCell>
                      {getLang(lang, "label.STATUS")}
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    {getLang(lang, "label.WON_DATE")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.WINNER_NAME")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.WINNER_PHONE_NUMBER")}
                  </StyledTableCell>
                  {/* Email - Show only in UNCLAIMED PRIZE tab */}

                  <StyledTableCell>
                    {getLang(lang, "label.WINNER_EMAIL")}
                  </StyledTableCell>

                  {/* Address - Show only in UNCLAIMED PRIZE tab */}

                  <StyledTableCell>
                    {getLang(lang, "label.WINNER_ADDRESS")}
                  </StyledTableCell>

                  {/* Remark - Show in both tabs */}
                  <StyledTableCell>
                    {getLang(lang, "label.REMARK") || "Remark"}
                  </StyledTableCell>

                  {/* Action - Show in both tabs */}
                  <StyledTableCell style={{ textAlign: "center" }}>
                    {getLang(lang, "label.ACTION")}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedWinners.map((winner, index) => (
                  <StyledTableRow
                    key={winner.uuid || index}
                    hover
                    tabIndex={-1}
                  >
                    {/* Checkbox - Show only in TO PROCESS tab */}
                    {activeTab === 0 && (
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(winner.uuid)}
                          onChange={() => handleSelectRow(winner.uuid)}
                        />
                      </StyledTableCell>
                    )}
                    <StyledTableCell>{winner.prizeName}</StyledTableCell>
                    <StyledTableCell>{getPrizeType(winner)}</StyledTableCell>
                    {/* Claimed Date - Show only in TO PROCESS tab */}
                    {activeTab === 0 && (
                      <StyledTableCell>
                        {formatDate(winner.claimed_date)}
                      </StyledTableCell>
                    )}
                    {/* Status - Show only in TO PROCESS tab */}
                    {activeTab === 0 && (
                      <StyledTableCell>
                        {getStatusComponent(winner)}
                      </StyledTableCell>
                    )}
                    <StyledTableCell>
                      {formatDate(winner.won_date)}
                    </StyledTableCell>
                    <StyledTableCell>{getWinnerName(winner)}</StyledTableCell>
                    <StyledTableCell>
                      {formatPhoneNumber(winner.participant?.contact_no)}
                    </StyledTableCell>
                    {/* Email - Show only in UNCLAIMED PRIZE tab */}

                    <StyledTableCell>
                      {winner.participant?.email || "-"}
                    </StyledTableCell>

                    {/* Address - Show only in UNCLAIMED PRIZE tab */}

                    <StyledTableCell>
                      {winner.participant?.address || "-"}
                    </StyledTableCell>

                    {/* Remark - Show in both tabs */}
                    <StyledTableCell>
                      {winner.admin_notes || "-"}
                    </StyledTableCell>

                    {/* Action - Show in both tabs */}
                    <StyledTableCell>
                      <Box display="flex" justifyContent="center">
                        <ActionButton
                          actionItems={getActionItems(winner)}
                          popperStyle={{ zIndex: 999 }}
                        />
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalCount || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </>
      ) : (
        <Box className={classes.emptyContainer}>
          <Typography variant="body1">
            {getLang(lang, "paragraph.NO_WINNER")}
          </Typography>
        </Box>
      )}
    </>
  );
}
